import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Heart, Loader2, Mail, X, Home } from 'lucide-react'
import { Card } from '@/components/ui'
import { Button } from '@/components/ui/Button'
import { useAuthStore } from '@/store'
import { supabase } from '@/lib/supabase'

const CALLBACK_TIMEOUT_MS = 12000

type VerifyState = 'processing' | 'success' | 'error'
type ErrorKind = 'expired' | 'already_used' | 'mismatch' | 'generic'

const errorTitles: Record<ErrorKind, string> = {
  expired: 'Ссылка истекла',
  already_used: 'Ссылка уже использована',
  mismatch: 'Ссылка не подходит',
  generic: 'Ошибка подтверждения'
}

const errorDescriptions: Record<ErrorKind, string> = {
  expired: 'Срок действия ссылки истек.',
  already_used: 'Эта ссылка уже была использована.',
  mismatch: 'Email в ссылке не совпадает с email в вашем профиле.',
  generic: 'Не удалось подтвердить email.'
}

const callbackErrorMessageMap: Record<string, string> = {
  invalid_supabase_session: 'Ссылка недействительна или устарела.',
  verification_flow_unavailable: 'Эта ссылка подтверждения уже не актуальна.',
  email_mismatch: 'Email в ссылке не совпадает с сохраненным в профиле.',
  application_user_not_found: 'Пользователь не найден. Выполните вход через Telegram и привяжите email.'
}

const errorCodeToKind: Record<string, ErrorKind> = {
  invalid_supabase_session: 'expired',
  verification_flow_unavailable: 'already_used',
  email_mismatch: 'mismatch'
}

export function EmailVerifiedPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { login, user } = useAuthStore()
  const [state, setState] = useState<VerifyState>('processing')
  const [errorKind, setErrorKind] = useState<ErrorKind>('generic')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const handledRef = useRef(false)

  useEffect(() => {
    if (handledRef.current) return

    // Check for Supabase error params in URL (e.g. expired link)
    const errorCode = searchParams.get('error_code')
    const errorDescription = searchParams.get('error_description') || searchParams.get('error')
    if (errorCode || errorDescription) {
      handledRef.current = true
      if (errorCode === 'otp_expired' || /expired/i.test(errorDescription || '')) {
        setErrorKind('expired')
        setErrorMessage(errorDescriptions.expired)
      } else if (errorCode === 'otp_disabled' || /used/i.test(errorDescription || '')) {
        setErrorKind('already_used')
        setErrorMessage(errorDescriptions.already_used)
      } else {
        setErrorKind('generic')
        setErrorMessage(errorDescription || errorDescriptions.generic)
      }
      setState('error')
      return
    }

    // Session detection & exchange
    let cancelled = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const markHandled = () => {
      handledRef.current = true
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    const exchangeSession = async (accessToken: string) => {
      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/email-auth-callback`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: `Bearer ${accessToken}`
          },
          body: JSON.stringify({ intent: 'email-verify' })
        })

        const payload = await response.json().catch(() => ({}))
        if (!response.ok) {
          const apiErrorCode = typeof payload?.error_code === 'string' ? payload.error_code : null
          const kind = (apiErrorCode && errorCodeToKind[apiErrorCode]) || 'generic'
          const message = (apiErrorCode && callbackErrorMessageMap[apiErrorCode]) || payload?.error || errorDescriptions.generic
          setErrorKind(kind)
          setErrorMessage(message)
          setState('error')
          await supabase.auth.signOut()
          return
        }

        // Update user state with verified email
        if (user && payload.user) {
          login({
            ...user,
            email: payload.user.email,
            email_verified_at: payload.user.email_verified_at,
            auth_method: payload.user.auth_method,
            updated_at: new Date().toISOString()
          })
        }
        await supabase.auth.signOut()
        setState('success')
      } catch {
        setErrorKind('generic')
        setErrorMessage('Ошибка при подтверждении email. Попробуйте позже.')
        setState('error')
        await supabase.auth.signOut()
      }
    }

    const getAccessTokenFromHash = (): string | null => {
      if (typeof window === 'undefined') return null
      const rawHash = window.location.hash?.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash
      if (!rawHash) return null
      return new URLSearchParams(rawHash).get('access_token')
    }

    const unsubscribe = supabase.auth.onAuthStateChange(async (event, session) => {
      if (cancelled || handledRef.current) return
      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') && session?.access_token) {
        markHandled()
        await exchangeSession(session.access_token)
      }
    }).data.subscription

    const checkExistingSession = async () => {
      if (cancelled || handledRef.current) return

      const { data: { session } } = await supabase.auth.getSession()
      if (session?.access_token) {
        markHandled()
        await exchangeSession(session.access_token)
        return
      }

      // PKCE fallback
      const code = searchParams.get('code')
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code)
        if (!error) {
          const { data: { session: codeSession } } = await supabase.auth.getSession()
          if (codeSession?.access_token && !cancelled && !handledRef.current) {
            markHandled()
            await exchangeSession(codeSession.access_token)
            return
          }
        }
      }

      // Hash fragment fallback
      const hashToken = getAccessTokenFromHash()
      if (hashToken && !cancelled && !handledRef.current) {
        markHandled()
        await exchangeSession(hashToken)
      }
    }

    timeoutId = setTimeout(() => {
      if (cancelled || handledRef.current) return
      setErrorKind('generic')
      setErrorMessage('Не удалось обработать ссылку подтверждения. Войдите через Telegram и запросите новую ссылку.')
      setState('error')
    }, CALLBACK_TIMEOUT_MS)

    void checkExistingSession()

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
      unsubscribe.unsubscribe()
    }
  }, [searchParams])

  const handleCloseWindow = () => {
    let closed = false
    try {
      window.close()
      closed = window.closed
    } catch {
      // ignore close errors in restricted browser contexts
    }
    if (!closed) {
      navigate('/login', { replace: true })
    }
  }

  if (state === 'processing') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center">
          <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium mb-2">Подтверждаем email</h3>
          <p className="text-muted-foreground">Обрабатываем ссылку, пожалуйста, подождите...</p>
        </Card>
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center p-6">
          <Mail className="w-12 h-12 text-error mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">{errorTitles[errorKind]}</h3>
          <p className="text-muted-foreground mb-2">{errorMessage}</p>
          <p className="text-xs text-muted-foreground mb-6">
            Войдите через Telegram и запросите новую ссылку подтверждения.
          </p>
          <div className="space-y-2">
            <Button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full"
            >
              На страницу входа
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  // state === 'success'
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center p-6">
        <Heart className="w-12 h-12 text-primary mx-auto mb-4" />
        <h1 className="text-xl font-semibold mb-2">Email подтвержден</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Адрес успешно подтвержден. Теперь вы можете использовать его для входа в приложение.
        </p>
        <div className="space-y-2">
          <Button
            onClick={() => navigate('/login', { replace: true })}
            className="w-full inline-flex items-center justify-center"
          >
            <Home className="w-4 h-4" />
            На страницу входа
          </Button>
          <Button variant="secondary" onClick={handleCloseWindow} className="w-full inline-flex items-center justify-center">
            <X className="w-4 h-4" />
            Закрыть окно
          </Button>
        </div>
      </Card>
    </div>
  )
}
